import { useCallback, useEffect, useState } from 'react';
import { CheckCircle2, Search, ShieldCheck, XCircle } from 'lucide-react';
import { toast } from 'sonner';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Textarea } from '@/components/ui/textarea';
import {
  fetchActorCanManageSeoApproval,
  fetchApproverDisplayName,
  getSeoApproval,
  setSeoApproval,
  type SeoApprovalState
} from '@/services/seoApprovalService';

interface ProfileSeoApprovalPanelProps {
  targetUserId: string;
  published: boolean;
  hasUnsavedChanges: boolean;
}

/**
 * Admin control for the SEO indexing approval key — the administrator half of
 * the two-key public-profile indexability gate. Renders NOTHING for
 * non-admins and never calls the approval RPCs for them. Three deliberately
 * separate states are shown: Published (profile visibility), Approved for
 * indexing (this key), and build verification (not yet connected — the
 * Frontfacing build result is not reported back anywhere today).
 */
export function ProfileSeoApprovalPanel({
  targetUserId,
  published,
  hasUnsavedChanges
}: ProfileSeoApprovalPanelProps) {
  const [canManage, setCanManage] = useState<boolean | null>(null);
  const [state, setState] = useState<SeoApprovalState | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [approverName, setApproverName] = useState<string | null>(null);
  const [note, setNote] = useState('');
  const [mutating, setMutating] = useState(false);

  useEffect(() => {
    let cancelled = false;

    // A new target officer must never inherit the previous officer's
    // approval state, note draft, error, or approver name — a stale state
    // rendered against the new target id would risk wrong-officer decisions
    // during route transitions.
    setState(null);
    setNote('');
    setLoadError(null);
    setApproverName(null);
    setMutating(false);

    const load = async () => {
      const allowed = await fetchActorCanManageSeoApproval();
      if (cancelled) {
return;
}
      setCanManage(allowed);
      if (!allowed) {
return;
}

      const result = await getSeoApproval(targetUserId);
      if (cancelled) {
return;
}
      if (!result.ok) {
        setLoadError(
          result.reason === 'forbidden'
            ? 'SEO indexing approvals are admin-only.'
            : 'Could not load the SEO indexing approval state.'
        );
        return;
      }
      setState(result.state);
      if (result.state.approvedBy) {
        const name = await fetchApproverDisplayName(result.state.approvedBy);
        if (!cancelled) {
setApproverName(name);
}
      }
    };

    void load();
    return () => {
      cancelled = true;
    };
  }, [targetUserId]);

  const applyDecision = useCallback(
    async (approved: boolean) => {
      setMutating(true);
      const trimmed = note.trim();
      const result = await setSeoApproval(targetUserId, approved, trimmed === '' ? null : trimmed);
      setMutating(false);

      if (!result.ok) {
        // Keep the previously loaded state untouched on failure.
        toast.error(
          result.reason === 'forbidden'
            ? 'SEO indexing approvals are admin-only.'
            : 'Failed to record the SEO indexing decision.'
        );
        return;
      }

      setState(result.state);
      setNote('');
      if (result.state.approvedBy) {
        setApproverName(await fetchApproverDisplayName(result.state.approvedBy));
      } else {
        setApproverName(null);
      }
      toast.success(approved ? 'Approved for search indexing' : 'Search indexing approval revoked');
    },
    [note, targetUserId]
  );

  // Non-admins (and the pre-check window) render nothing at all.
  if (canManage !== true) {
return null;
}

  const decidedAt = state?.decidedAt ? new Date(state.decidedAt).toLocaleString() : null;
  const statusBadge =
    state === null
      ? null
      : state.status === 'approved'
        ? { label: 'Approved for indexing', variant: 'default' as const }
        : state.status === 'not_approved'
          ? { label: 'Not approved', variant: 'secondary' as const }
          : { label: 'Never reviewed', variant: 'outline' as const };

  return (
    <Card className="border-primary/20 bg-primary/[0.025]" data-testid="seo-approval-panel">
      <CardHeader className="gap-3 sm:flex-row sm:items-start sm:justify-between">
        <div>
          <div className="flex flex-wrap items-center gap-2">
            <ShieldCheck className="h-5 w-5 text-primary" aria-hidden="true" />
            <CardTitle className="text-lg">Search indexing approval</CardTitle>
            {statusBadge && <Badge variant={statusBadge.variant}>{statusBadge.label}</Badge>}
          </div>
          <CardDescription className="mt-2">
            Administrator key of the two-key indexing gate. Public officer pages stay
            non-indexable until an administrator approves them AND the public site&apos;s
            automated content checks pass on its next build.
          </CardDescription>
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="grid gap-2 sm:grid-cols-3">
          <div className="flex items-start gap-3 rounded-lg border bg-background p-3">
            {published ? (
              <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0 text-success" aria-hidden="true" />
            ) : (
              <XCircle className="mt-0.5 h-4 w-4 shrink-0 text-muted-foreground" aria-hidden="true" />
            )}
            <div>
              <p className="text-sm font-medium">{published ? 'Published' : 'Not published'}</p>
              <p className="mt-1 text-xs text-muted-foreground">Profile visibility</p>
            </div>
          </div>
          <div className="flex items-start gap-3 rounded-lg border bg-background p-3">
            {state?.status === 'approved' ? (
              <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0 text-success" aria-hidden="true" />
            ) : (
              <XCircle className="mt-0.5 h-4 w-4 shrink-0 text-muted-foreground" aria-hidden="true" />
            )}
            <div>
              <p className="text-sm font-medium">
                {state === null
                  ? 'Loading…'
                  : state.status === 'approved'
                    ? 'Approved for indexing'
                    : state.status === 'not_approved'
                      ? 'Not approved'
                      : 'Never reviewed'}
              </p>
              <p className="mt-1 text-xs text-muted-foreground">Administrator decision</p>
            </div>
          </div>
          <div className="flex items-start gap-3 rounded-lg border bg-background p-3">
            <Search className="mt-0.5 h-4 w-4 shrink-0 text-muted-foreground" aria-hidden="true" />
            <div>
              <p className="text-sm font-medium">Build verification</p>
              <p className="mt-1 text-xs text-muted-foreground">Not yet connected</p>
            </div>
          </div>
        </div>

        {loadError && <p className="text-sm text-destructive">{loadError}</p>}

        {state && (
          <>
            {(state.status !== 'never_reviewed') && (
              <div className="rounded-lg border bg-background p-3">
                <p className="text-sm font-medium">Last decision</p>
                <p className="mt-1 text-sm text-muted-foreground">
                  {state.approved ? 'Approved' : 'Not approved'}
                  {decidedAt ? ` on ${decidedAt}` : ''}
                  {` by ${approverName ?? 'an administrator'}`}
                  {state.note ? ` — “${state.note}”` : ''}
                </p>
              </div>
            )}

            <div className="space-y-2">
              <label className="text-sm font-medium" htmlFor="seo-approval-note">
                Decision note
              </label>
              <Textarea
                id="seo-approval-note"
                value={note}
                onChange={(event) => setNote(event.target.value)}
                placeholder="Why this profile should (or should not) be indexed"
                disabled={mutating || hasUnsavedChanges}
                rows={2}
              />
            </div>

            <div className="flex flex-wrap items-center gap-2">
              <Button
                type="button"
                disabled={mutating || hasUnsavedChanges || state.approved}
                onClick={() => void applyDecision(true)}
                className="gap-2"
              >
                <CheckCircle2 className="h-4 w-4" aria-hidden="true" />
                {mutating ? 'Saving…' : 'Approve for indexing'}
              </Button>
              <Button
                type="button"
                variant="outline"
                disabled={mutating || hasUnsavedChanges || !state.approved}
                onClick={() => void applyDecision(false)}
                className="gap-2"
              >
                <XCircle className="h-4 w-4" aria-hidden="true" />
                {mutating ? 'Saving…' : 'Revoke approval'}
              </Button>
            </div>

            {hasUnsavedChanges && (
              <p className="text-sm text-warning">
                Save the current profile changes before changing the indexing decision —
                approval applies to the profile as saved.
              </p>
            )}
            <p className="text-xs text-muted-foreground">
              Unpublishing does not revoke approval. The public site only indexes a page when
              it is published, approved here, and passing the automated content checks.
            </p>
          </>
        )}
      </CardContent>
    </Card>
  );
}
